/**
 * Plain-language helpers for Property DNA — not legal advice; contextualizes open-data codes.
 */

function safeStr(v) {
  return String(v ?? "").trim();
}

export function explainLandUseDesignation(raw) {
  const s = safeStr(raw);
  if (!s) return { headline: "", detail: "" };
  const u = s.toUpperCase();
  if (/^R-/.test(u)) {
    return {
      headline: "Residential land use district",
      detail:
        "Calgary groups most housing into “R-” districts. Each code sets rules for building types, height, setbacks, and what uses are allowed. The official Land Use Bylaw is the source of truth for any project.",
    };
  }
  if (/^C-/.test(u) || /^CR-/.test(u) || /^CC-/.test(u)) {
    return {
      headline: "Commercial / mixed context",
      detail:
        "This label often appears near main streets or mixed areas. Uses and density depend on the exact district — check the City’s bylaws or a planner for a specific proposal.",
    };
  }
  if (/^I-/.test(u) || /^IH$/.test(u) || /^IM$/.test(u)) {
    return {
      headline: "Industrial or employment area",
      detail:
        "Industrial districts prioritize employment and logistics. Housing is usually not the primary intent unless a specific redesignation applies.",
    };
  }
  if (/^M-/.test(u) || /^H-GO/.test(u)) {
    return {
      headline: "Special or contextual district",
      detail:
        "Some codes cover grade-oriented, high-density, or special policy areas. Interpretation depends on the full bylaw text and site context.",
    };
  }
  return {
    headline: "Land use label (City dataset)",
    detail:
      "This text comes from the City’s assessment/open-data feed. For what you can build or change on a lot, rely on the current Land Use Bylaw and any active redesignations — not this summary alone.",
  };
}

export function explainAssessmentClass(description, code) {
  const d = safeStr(description) || safeStr(code);
  if (!d) return { headline: "", detail: "" };
  const low = d.toLowerCase();
  if (low.includes("residential")) {
    return {
      headline: "Residential assessment class",
      detail:
        "The assessment roll classifies the parcel for municipal valuation. It helps compare similar properties — it is not the same as zoning, and it does not decide legal use by itself.",
    };
  }
  if (low.includes("non-res") || low.includes("commercial") || low.includes("industrial")) {
    return {
      headline: "Non-residential roll class",
      detail:
        "Non-residential classes cover retail, office, industrial, and similar uses in the assessment system. Tax and valuation rules differ from residential.",
    };
  }
  return {
    headline: "Assessment class (roll)",
    detail:
      "The City’s open assessment data assigns a class for valuation. Confirm details on the official assessment notice if you need authoritative wording.",
  };
}

export function explainPropertyTypeCode(raw) {
  const s = safeStr(raw);
  if (!s) return { headline: "", detail: "" };
  const u = s.toUpperCase();
  if (u === "LI") {
    return {
      headline: "Residential improvement (LI)",
      detail:
        "City assessment code LI marks a residential improvement on the roll — typically a house or similar dwelling. It describes how the parcel is classified for valuation, not zoning or legal use.",
    };
  }
  const low = s.toLowerCase();
  if (low.includes("detached") || low.includes("single")) {
    return {
      headline: "Detached / single-family style (dataset)",
      detail:
        "The roll’s property-type field describes the improved structure at a high level. It may not capture every legal suite or recent renovation.",
    };
  }
  if (low.includes("condo") || low.includes("apartment") || low.includes("multi")) {
    return {
      headline: "Multi-unit / strata context (dataset)",
      detail:
        "Condos and apartments are often valued in the roll with unit-level or complex-level records. Use this as orientation, not a condo plan substitute.",
    };
  }
  return {
    headline: "Property type (open data)",
    detail:
      "A short code from the assessment dataset describing the improvement. Pair with zoning and condo documents for a full picture.",
  };
}

/**
 * Plain-language positioning of one assessed value vs community roll stats for a **peer group**
 * (same sub_property_use pattern — not all RE mixed).
 * @param {object} [opts]
 * @param {string} [opts.peerGroupLabel] — e.g. "Detached houses"
 * @param {boolean} [opts.broadMixedRE] — true if all RE types were mixed (sub code missing)
 */
export function marketPositionNarrative(assessed, community, opts = {}) {
  const a = Number(assessed);
  const min = Number(community?.min_assessed);
  const max = Number(community?.max_assessed);
  const avg = Number(community?.avg_assessed);
  const name = safeStr(community?.comm_name) || "this community";
  const peer = safeStr(opts.peerGroupLabel);
  const peerPhrase = peer ? `${peer} in ${name}` : `similar residential roll types in ${name}`;
  const broad = opts.broadMixedRE === true;

  if (!Number.isFinite(a) || !Number.isFinite(avg)) {
    return "Community averages are loading or unavailable — when present, they use the same peer group as the stats card (assessment roll, not MLS).";
  }

  if (broad) {
    return `This comparison mixes all residential (RE) roll rows in ${name} because a sub-property code was missing — use it only as rough context until the parcel record is complete.`;
  }

  if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
    if (a < min) {
      return `The dataset assessment for this parcel is below the lowest value in the peer group (${peerPhrase}) — unusual; confirm against your official notice.`;
    }
    if (a > max) {
      return `The dataset assessment is above the highest value in the peer group (${peerPhrase}) — luxury product or large lots can sit above typical bands.`;
    }
    const span = max - min;
    const t = span > 0 ? (a - min) / span : 0.5;
    if (t < 0.25) {
      return `Compared with ${peerPhrase}, this assessment sits toward the lower end of the min–max band (not a price prediction — assessment ≠ market value).`;
    }
    if (t > 0.75) {
      return `Compared with ${peerPhrase}, this assessment sits toward the upper end of the band — useful context, not a listing price.`;
    }
    return `Compared with ${peerPhrase}, this assessment sits near the middle of the min–max band from open data — directional context only.`;
  }

  if (a < avg) {
    return `This parcel’s dataset assessment is below the peer-group average (${peerPhrase}).`;
  }
  if (a > avg) {
    return `This parcel’s dataset assessment is above the peer-group average (${peerPhrase}).`;
  }
  return `This parcel’s dataset assessment is close to the peer-group average (${peerPhrase}) — still not the same as market value.`;
}
